/**
 * AssetGrid + KindTile + ShareSummary — small Workspace render helpers.
 * Phase 7 extract from pages/Workspace.jsx.
 */
import React from "react";
import { Badge } from "@/components/ui/badge";
import {
    Folder, Database, LayoutDashboard, Images,
    Share2, Users as UsersIcon, Lock, Globe2, AlertCircle, Check,
} from "lucide-react";

const ASSET_ICON = { dashboard: LayoutDashboard, dataset: Database, gallery: Images };

export function AssetGrid({ items, onOpen, onShare, viewMode = "tiles" }) {
    if (items.length === 0) {
        return <div className="text-center py-16 text-sm text-muted-foreground" data-testid="assets-empty">No assets in this workspace yet.</div>;
    }
    if (viewMode === "details") {
        return (
            <div className="border border-border rounded-sm bg-card overflow-hidden" data-testid="asset-list">
                 <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-secondary/30">
                        <tr>
                            <th className="text-left px-3 py-2 eyebrow">Name</th>
                            <th className="text-left px-3 py-2 eyebrow w-28">Type</th>
                            <th className="text-left px-3 py-2 eyebrow w-40">Shared by</th>
                            <th className="text-left px-3 py-2 eyebrow w-32">Sharing</th>
                            <th className="w-12" />
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((i) => {
                            const Icon = ASSET_ICON[i._type] || Folder;
                            const hasOverride = i.share_mode && i.share_mode !== "inherit";
                            const sharedBy = i.created_by_name || i.owner_name || i.created_by_email || "—";
                            return (
                                <tr key={`${i._type}-${i.id}`} className="border-t border-border hover:bg-secondary/30 cursor-pointer" onClick={() => onOpen(i)} data-testid={`asset-row-${i.id}`}>
                                    <td className="px-3 py-2">
                                        <div className="flex items-center gap-2">
                                            <Icon className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.75} />
                                            <span className="truncate">{i.name}</span>
                                        </div>
                                    </td>
                                    <td className="px-3 py-2 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">{i._type}</td>
                                    <td className="px-3 py-2 text-[11px] font-mono text-muted-foreground truncate">{sharedBy}</td>
                                    <td className="px-3 py-2 text-[11px] font-mono text-muted-foreground">{hasOverride ? "custom" : "inherited"}</td>
                                    <td className="px-3 py-2 text-right">
                                        {onShare && (
                                            <button onClick={(e) => { e.stopPropagation(); onShare(i); }}
                                                    className="h-7 w-7 flex items-center justify-center rounded-sm text-muted-foreground hover:text-foreground hover:bg-secondary"
                                                    title="Share this asset"
                                                    data-testid={`asset-share-${i.id}`}>
                                                <Share2 className="h-3.5 w-3.5" />
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                </div>
            </div>
        );
    }
    return (
       <div className="grid grid-cols-1 sm:grid-cols-2 2xl:grid-cols-3 gap-3" data-testid="asset-grid">
            {items.map((i) => {
                const Icon = ASSET_ICON[i._type] || Folder;
                const hasOverride = i.share_mode && i.share_mode !== "inherit";
                return (
                   <div key={`${i._type}-${i.id}`} className="group relative text-left border border-border bg-card rounded-sm p-4 hover-lift overflow-hidden" data-testid={`asset-card-${i.id}`}>
                        <button onClick={() => onOpen(i)} className="w-full text-left">
                            <div className="flex items-start gap-3">
                                <div className="w-9 h-9 bg-secondary flex items-center justify-center shrink-0">
                                    <Icon className="h-4 w-4" strokeWidth={1.75} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium truncate">{i.name}</div>
                                     <div className="text-[11px] font-mono text-muted-foreground mt-0.5 uppercase tracking-wider flex flex-wrap items-center gap-1.5 min-w-0">
                                        <span className="shrink-0">{i._type}</span>
                                        {hasOverride && (
                                            <span className="text-primary whitespace-nowrap truncate min-w-0">· custom share</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </button>
                        {onShare && (
                            <button onClick={(e) => { e.stopPropagation(); onShare(i); }}
                                    className="absolute top-2 right-2 h-7 w-7 flex items-center justify-center rounded-sm text-muted-foreground hover:text-foreground hover:bg-secondary opacity-0 group-hover:opacity-100 transition-opacity"
                                    title="Share this asset"
                                    data-testid={`asset-share-${i.id}`}>
                                <Share2 className="h-3.5 w-3.5" />
                            </button>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

export function KindTile({ label, icon: Icon, active, desc, onClick, testId }) {
    return (
        <button type="button" onClick={onClick} data-testid={testId}
                className={`border rounded-sm p-3 text-left transition-colors ${active ? "border-primary bg-primary/5" : "border-border hover:border-foreground/30"}`}>
            <Icon className={`h-4 w-4 mb-1.5 ${active ? "text-primary" : "text-muted-foreground"}`} />
            <div className="text-sm font-medium">{label}</div>
            <div className="text-[11px] text-muted-foreground mt-0.5">{desc}</div>
        </button>
    );
}

export function ShareModeRow({ active, onClick, icon: Icon, title, desc, testId }) {
    return (
        <button type="button" onClick={onClick} data-testid={testId}
                className={`w-full flex items-start gap-3 p-3 border rounded-sm text-left transition-colors ${active ? "border-primary bg-primary/5" : "border-border hover:border-foreground/30"}`}>
            <Icon className={`h-4 w-4 mt-0.5 ${active ? "text-primary" : "text-muted-foreground"}`} />
            <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">{title}</div>
                <div className="text-[11px] text-muted-foreground mt-0.5">{desc}</div>
            </div>
            {active && <Check className="h-4 w-4 text-primary shrink-0" />}
        </button>
    );
}

export function ShareSummary({ item, users }) {
    const mode = item.share_mode || (item.kind === "shared" ? "tenant" : "private");
    if (mode === "private") return (
        <div className="flex items-center gap-2 text-sm"><Lock className="h-3.5 w-3.5 text-muted-foreground" /><span>Private — only you</span></div>
    );
    if (mode === "tenant") return (
        <div className="flex items-center gap-2 text-sm"><Globe2 className="h-3.5 w-3.5 text-muted-foreground" /><span>Everyone in this entity</span></div>
    );
    if (mode === "inherit") return (
        <div className="flex items-center gap-2 text-sm text-muted-foreground"><AlertCircle className="h-3.5 w-3.5" /><span>Inherits from workspace</span></div>
    );
    const count = (item.shared_with || []).length;
    return (
        <div data-testid="share-summary-members">
            <div className="flex items-center gap-2 text-sm mb-2"><UsersIcon className="h-3.5 w-3.5 text-muted-foreground" /><span>Shared with {count} {count === 1 ? "person" : "people"}</span></div>
            <div className="flex flex-wrap gap-1">
                {(item.shared_with || []).slice(0, 6).map((s) => {
                    const u = users.find((x) => x.id === s.user_id);
                    return (
                        <Badge key={s.user_id} variant="outline" className="font-mono text-[10px]">
                            {u?.name || s.user_id.slice(0, 6)}{s.permission === "edit" ? " ✎" : ""}
                        </Badge>
                    );
                })}
            </div>
        </div>
    );
}
